import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FiCheck, FiArrowRight, FiArrowLeft } from 'react-icons/fi'

const PRO_FEATURES = [
  'Fichaje por app móvil y web',
  'PIN personal por empleado',
  'Reportes mensuales en PDF',
  'Auditoría inmutable RD-Ley 8/2019',
  'Conservación de registros 4 años',
  'Soporte por email en español',
  'Servidores en la UE (RGPD)',
  'Sin permanencia ni mínimos',
]

const AVANZADO_FEATURES = [
  'Todo lo del plan Pro, y además:',
  'Geolocalización al fichar',
  'Códigos QR físicos por centro',
  'Fichaje grupal (quiosco compartido)',
  'Gestión de ausencias y vacaciones',
  'Solicitudes de horas extras con flujo de aprobación',
  'Múltiples centros de trabajo y calendarios',
  'Tipos de pausa y ausencia personalizables',
  'Acceso de asesoría a múltiples empresas cliente',
]

export default function Precios() {
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://presenzia.app/landing" },
      { "@type": "ListItem", "position": 2, "name": "Precios", "item": "https://presenzia.app/precios" },
    ],
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "PresenzIA - Software de control horario",
    "description": "Software de control horario y fichaje digital para pymes en España.",
    "brand": { "@type": "Brand", "name": "PresenzIA" },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "EUR",
      "lowPrice": "1.25",
      "highPrice": "2.95",
      "offerCount": 4,
      "offers": [
        { "@type": "Offer", "name": "Pro mensual", "price": "1.50", "priceCurrency": "EUR" },
        { "@type": "Offer", "name": "Pro anual", "price": "1.25", "priceCurrency": "EUR" },
        { "@type": "Offer", "name": "Avanzado mensual", "price": "2.95", "priceCurrency": "EUR" },
        { "@type": "Offer", "name": "Avanzado anual", "price": "2.50", "priceCurrency": "EUR" }
      ]
    }
  }

  return (
    <>
      <Helmet>
        <title>Precios desde 1,25 €/empleado/mes | PresenzIA</title>
        <meta name="description" content="Precios de PresenzIA: control horario para pymes desde 1,25 €/empleado/mes (pago anual). Plan Pro y Avanzado. Sin mínimos, sin permanencia, 14 días gratis sin tarjeta." />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://presenzia.app/precios" />
        <meta property="og:title" content="Precios desde 1,25 €/empleado/mes | PresenzIA" />
        <meta property="og:description" content="Control horario para pymes desde 1,25 €/empleado/mes. Sin mínimos. 14 días gratis." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://presenzia.app/precios" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="PresenzIA" />
        <meta property="og:image" content="https://presenzia.app/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Precios desde 1,25 €/empleado/mes | PresenzIA" />
        <meta name="twitter:description" content="Control horario para pymes desde 1,25 €/empleado/mes. Sin mínimos. 14 días gratis." />
        <meta name="twitter:image" content="https://presenzia.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-gray-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
            <Link to="/landing" className="flex items-center gap-2">
              <img src="/logo-presenzia-icon.svg" alt="PresenzIA" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-bold text-gray-900">
                Presenz<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">IA</span>
              </span>
            </Link>
            <Link to="/registro" className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Empieza gratis 14 días <FiArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
          <Link to="/landing" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600">
            <FiArrowLeft className="h-3.5 w-3.5" /> Volver a inicio
          </Link>
        </div>

        <header className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 pb-10 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
            Precios sencillos. Por empleado, sin mínimos.
          </h1>
          <p className="text-lg text-gray-600">
            Sólo pagas por los empleados que tengas activos cada mes. 14 días gratis sin
            tarjeta. Onboarding asistido sin coste; migración de registros históricos bajo presupuesto.
          </p>
        </header>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16 grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl bg-white border border-gray-200 p-8 shadow-sm">
            <div className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-2">Plan Pro</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Pro</h2>
            <p className="text-sm text-gray-600 mb-5">Para pymes que sólo necesitan cumplir la ley sin complicarse.</p>
            <div className="mb-1">
              <span className="text-5xl font-extrabold text-gray-900">1,25 €</span>
              <span className="text-gray-500"> /empleado/mes</span>
            </div>
            <div className="text-xs text-gray-500 mb-6">Pago anual · 1,50 €/mes con pago mensual</div>
            <Link
              to="/registro?plan=pro"
              className="block text-center w-full px-5 py-3 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800"
            >
              Empezar 14 días gratis
            </Link>
            <ul className="mt-6 space-y-2.5 text-sm">
              {PRO_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <FiCheck className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl bg-gradient-to-br from-indigo-600 to-cyan-500 p-8 shadow-xl text-white relative overflow-hidden">
            <div className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider bg-white/20 backdrop-blur px-2.5 py-1 rounded-full">
              Recomendado
            </div>
            <div className="text-xs font-mono uppercase tracking-wider text-white/70 mb-2">Plan Avanzado</div>
            <h2 className="text-2xl font-bold mb-1">Avanzado</h2>
            <p className="text-sm text-white/80 mb-5">Para empresas con varias ubicaciones, personal en ruta o asesorías.</p>
            <div className="mb-1">
              <span className="text-5xl font-extrabold">2,50 €</span>
              <span className="text-white/80"> /empleado/mes</span>
            </div>
            <div className="text-xs text-white/70 mb-6">Pago anual · 2,95 €/mes con pago mensual</div>
            <Link
              to="/registro?plan=avanzado"
              className="block text-center w-full px-5 py-3 rounded-lg bg-white text-indigo-700 font-semibold hover:bg-gray-50"
            >
              Empezar 14 días gratis
            </Link>
            <ul className="mt-6 space-y-2.5 text-sm">
              {AVANZADO_FEATURES.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-white">
                  <FiCheck className="h-4 w-4 text-white mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Preguntas frecuentes sobre el precio</h2>
          <div className="space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">¿Cómo se cobra?</h3>
              <p className="text-gray-600">Mensualmente, por número de empleados activos al inicio del periodo. Si das de baja a alguien a mitad de mes, el cargo se ajusta el mes siguiente.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">¿Hay permanencia?</h3>
              <p className="text-gray-600">No. Puedes cancelar en cualquier momento desde tu panel de suscripción. Si pagas anualmente y cancelas a mitad de año, conservas el acceso hasta el final del periodo.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">¿Y si tengo 1 empleado?</h3>
              <p className="text-gray-600">Pagas 1,25 € o 2,50 € al mes (según plan, anual). No hay mínimos. PresenzIA está pensado para autónomos con personal y micropymes desde el primer empleado.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">¿Aceptáis tarjeta y transferencia?</h3>
              <p className="text-gray-600">Tarjeta vía Stripe (procesador PCI-DSS). Para asesorías o cuentas grandes ofrecemos cobro por transferencia bancaria con factura — escríbenos a asesorias@presenzia.app.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">¿Qué incluye los 14 días gratis?</h3>
              <p className="text-gray-600">Acceso completo al plan Avanzado durante 14 días, sin tarjeta. Al expirar eliges el plan que prefieras o sigues como cliente Pro/Avanzado. No te cobramos nunca sin tu consentimiento expreso.</p>
            </div>
          </div>
        </section>

        <footer className="border-t border-gray-100 py-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-sm text-gray-500 flex flex-wrap items-center justify-between gap-4">
            <span>© 2026 PresenzIA · Hecho en España</span>
            <div className="space-x-4">
              <Link to="/aviso-legal" className="hover:text-blue-600">Aviso legal</Link>
              <Link to="/privacidad" className="hover:text-blue-600">Privacidad</Link>
              <Link to="/cookies" className="hover:text-blue-600">Cookies</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
